import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

export const usePublicTracking = () => {
  const [sessionId, setSessionId] = useState<string>('');
  
  useEffect(() => {
    let sid = sessionStorage.getItem('tracking_session_id');
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem('tracking_session_id', sid);
      trackSession(sid);
    }
    setSessionId(sid);
  }, []);
  
  const trackSession = async (sid: string) => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const deviceType = getDeviceType();
      
      await supabase.from('public_sessions').upsert({
        session_id: sid,
        first_page: window.location.pathname,
        referrer: document.referrer || null,
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        device_type: deviceType,
        first_seen_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking session:', error);
    }
  };
  
  const trackEvent = async (
    eventType: string,
    eventName?: string,
    elementId?: string,
    elementText?: string,
    coordinates?: { x: number; y: number },
    metadata?: any
  ) => {
    if (!sessionId) return;
    
    try {
      await supabase.from('public_page_events').insert({
        session_id: sessionId,
        event_type: eventType,
        page_path: window.location.pathname,
        event_name: eventName,
        element_id: elementId,
        element_text: elementText?.substring(0, 100),
        x_coordinate: coordinates?.x ? Math.round(coordinates.x) : undefined,
        y_coordinate: coordinates?.y ? Math.round(coordinates.y) : undefined,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        metadata: metadata
      });
      
      await supabase.from('public_sessions')
        .update({ last_seen_at: new Date().toISOString() })
        .eq('session_id', sessionId);
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  };
  
  const trackPageView = () => {
    trackEvent('page_view', `Visualizou ${window.location.pathname}`);
  };
  
  const trackClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    
    // Tentar múltiplas formas de identificar o elemento
    let elementId = target.id;
    if (!elementId && target.className) {
      // Usar as classes como ID se não tiver id
      elementId = target.className.split(' ').filter(c => c).slice(0, 3).join('.');
    }
    if (!elementId) {
      // Usar tagName como último recurso
      elementId = target.tagName.toLowerCase();
    }
    
    // Tentar múltiplas formas de capturar texto
    let elementText = target.innerText?.substring(0, 100);
    if (!elementText) elementText = target.getAttribute('aria-label');
    if (!elementText) elementText = target.getAttribute('title');
    if (!elementText) elementText = target.getAttribute('placeholder');
    if (!elementText && target.tagName === 'IMG') {
      elementText = (target as HTMLImageElement).alt || 'Imagem sem alt';
    }
    
    trackEvent(
      'click',
      'Click',
      elementId || undefined,
      elementText || undefined,
      { x: e.clientX, y: e.clientY + window.scrollY }
    );
  };
  
  const trackScroll = (sectionName: string) => {
    trackEvent('scroll', `Visualizou seção: ${sectionName}`);
  };
  
  const trackFormInteraction = (fieldName: string) => {
    trackEvent('form_interaction', `Interagiu com: ${fieldName}`, fieldName);
  };
  
  const trackConversion = async (conversionType: string) => {
    await trackEvent('form_submit', `Conversão: ${conversionType}`, undefined, undefined, undefined, { conversion_type: conversionType });
    
    await supabase.from('public_sessions')
      .update({ 
        converted: true, 
        conversion_type: conversionType 
      })
      .eq('session_id', sessionId);
  };
  
  return {
    trackPageView,
    trackClick,
    trackScroll,
    trackFormInteraction,
    trackConversion
  };
};
